import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { format } from 'date-fns';

import { config } from '../../api/config';
import { actions } from '../../state';
import { Paper, Toggle } from '../../components';
import Form from './banners.form';
import { validationErrors } from './banners.validation';
import {
  createBannerPayload,
  getBannerFieldsFromDiscount,
  getDefaultBannerValues,
} from './banners.utils';
import styles from './banners.module.scss';

const AddBanner = ({
  dispatch,
  history,
  banners: { loading, imageUploading },
  discounts: { discounts },
}) => {
  const [values, setValues] = useState(getDefaultBannerValues());
  const today = format(Date.now(), 'yyyy-MM-dd');

  useEffect(() => {
    dispatch(
      actions.discounts.getDiscountsAction({
        limit: 1000,
        offset: 0,
        order: [{ by: 'code', order: 'asc' }],
      })
    );
    return () => {
      dispatch(actions.banners.clearBannersAction());
    };
  }, [dispatch]);

  const discountOptions = useMemo(
    () => [
      { name: 'Pasirinkti', value: '' },
      ...discounts.map(discount => ({
        name:
          discount.code ||
          discount.title ||
          discount.description_lt ||
          `#${discount.id}`,
        value: discount.id,
      })),
    ],
    [discounts]
  );

  const selectedDiscount = useMemo(
    () =>
      discounts.find(
        discount => Number(discount.id) === Number(values.discountId)
      ) || null,
    [discounts, values.discountId]
  );
  const discountFieldsLocked = values.hasDiscount && Boolean(selectedDiscount);

  useEffect(() => {
    if (!discountFieldsLocked) return;

    const discountFields = getBannerFieldsFromDiscount(selectedDiscount);

    if (!discountFields) return;

    setValues(currentValues => ({
      ...currentValues,
      ...discountFields,
    }));
  }, [discountFieldsLocked, selectedDiscount]);

  const handleInputChange = ({ target: { name, value } }) => {
    setValues(currentValues => ({
      ...currentValues,
      [name]: value,
    }));
  };

  const handleActivationChange = () => {
    setValues(currentValues => ({
      ...currentValues,
      isActivated: !currentValues.isActivated,
    }));
  };

  const handleDiscountToggle = () => {
    setValues(currentValues => ({
      ...currentValues,
      hasDiscount: !currentValues.hasDiscount,
      discountId: '',
      deepLink: '',
    }));
  };

  const handleLocationToggle = locationValue => {
    setValues(currentValues => {
      const nextPlacements = currentValues.placements.includes(locationValue)
        ? currentValues.placements.filter(
            location => location !== locationValue
          )
        : [...currentValues.placements, locationValue];

      return {
        ...currentValues,
        placements: nextPlacements,
      };
    });
  };

  const handleFileUploaded = file => {
    if (!file) return;

    const imageUrl = URL.createObjectURL(file);
    setValues(currentValues => ({
      ...currentValues,
      image: {
        name: file.name,
        url: imageUrl,
      },
    }));
    dispatch(actions.banners.uploadBannerImageAction(file));
  };

  const handleFileDeleted = () => {
    setValues(currentValues => ({
      ...currentValues,
      image: null,
    }));
    dispatch(actions.banners.uploadBannerImageAction(null));
  };

  const handleSubmit = event => {
    event.preventDefault();

    const error = validationErrors(values, {
      requireFutureEndDate: true,
    });

    if (error) {
      dispatch(actions.error.setErrorAction({ msg: error }));
      return;
    }

    dispatch(
      actions.banners.addBannerAction(createBannerPayload(values), history)
    );
  };

  return (
    <section className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Banerio kūrimas</h1>
        <div className={styles.pageHeaderActions}>
          <div className={styles.toggleMeta}>
            <Toggle
              checked={values.isActivated}
              onChange={handleActivationChange}
            />
            <span>Aktyvuota</span>
          </div>
        </div>
      </div>

      <Paper size="sm" className={styles.card}>
        <Form
          values={values}
          onChange={handleInputChange}
          onSubmit={handleSubmit}
          onToggleDiscount={handleDiscountToggle}
          onLocationToggle={handleLocationToggle}
          onFileUploaded={handleFileUploaded}
          onFileDeleted={handleFileDeleted}
          discountOptions={discountOptions}
          discountFieldsLocked={discountFieldsLocked}
          loading={loading}
          imageUploading={imageUploading}
          dateToMin={today}
        />
      </Paper>
    </section>
  );
};

const mapStateToProps = state => ({
  banners: state.banners,
  discounts: state.discounts,
});

AddBanner.propTypes = {
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  banners: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    imageUploading: PropTypes.bool.isRequired,
  }).isRequired,
  discounts: PropTypes.shape({
    discounts: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
};

export default connect(mapStateToProps)(AddBanner);
